import { motion } from "framer-motion";
import { ArrowRight, Crown, Trophy } from "lucide-react";
import { BRAND } from "@/lib/brand";

const Ember = ({ delay, left }) => (
  <span
    className="ember"
    style={{ left: `${left}%`, animationDelay: `${delay}s` }}
  />
);

export default function Hero() {
  const embers = Array.from({ length: 18 }).map((_, i) => ({
    delay: (i * 0.6) % 8,
    left: (i * 7 + 3) % 100,
  }));

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={BRAND.heroBg}
          alt="Golden arena texture"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.12)_0%,transparent_60%)]" />
      </div>

      {/* Embers */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {embers.map((e, i) => (
          <Ember key={i} delay={e.delay} left={e.left} />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-[#D4AF37]/40 px-3 py-1 mb-6 cut-corners bg-black/40 backdrop-blur"
            data-testid="hero-overline"
          >
            <Crown size={14} className="text-[#D4AF37]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F3E5AB]">
              Esports • Studio • Community
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black uppercase leading-[0.92] tracking-tighter text-white"
            data-testid="hero-headline"
          >
            Forged in{" "}
            <span className="gold-text">Gold.</span>
            <br />
            Crowned in{" "}
            <span className="gold-text">Glory.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 font-body"
            data-testid="hero-subtitle"
          >
            The Golden Legends is a championship esports house, game studio, and
            community arena built around three thrones — Rocket League, Fortnite,
            and Minecraft. Train with legends. Compete for crowns.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#tournaments"
              data-testid="hero-cta-tournaments"
              className="btn-shimmer bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest text-xs sm:text-sm px-7 py-4 inline-flex items-center gap-2 cut-corners transition-colors"
            >
              <Trophy size={16} /> View Tournaments
              <ArrowRight size={16} />
            </a>
            <a
              href={BRAND.discordUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-discord"
              className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold uppercase tracking-widest text-xs sm:text-sm px-7 py-4 cut-corners transition-colors"
            >
              Join the Discord
            </a>
          </motion.div>

          <div
            className="mt-14 grid grid-cols-3 gap-6 max-w-md"
            data-testid="hero-stats"
          >
            {[
              { k: "12+", v: "Trophies" },
              { k: "10", v: "Members" },
              { k: "$100", v: "Prize Pool" },
            ].map((s) => (
              <div key={s.v} className="border-l border-[#D4AF37]/40 pl-3">
                <div className="font-display text-2xl sm:text-3xl font-black gold-text">
                  {s.k}
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Logo focal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-square max-w-md mx-auto">
            <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.45)_0%,rgba(212,175,55,0.15)_45%,transparent_72%)] blur-2xl" />
            <img
              src={BRAND.logoUrl}
              alt="The Golden Legends logo"
              className="relative w-full h-full object-cover rounded-full drop-shadow-[0_0_50px_rgba(212,175,55,0.55)]"
              style={{ mixBlendMode: "screen" }}
              data-testid="hero-brand-logo"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
